import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Service } from '@/api/services/servicesApi.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useServicesQuery } from '@/hooks/useServicesQuery';
import { useSetServiceActiveMutation } from '@/hooks/useServiceMutations';
import { buildTherapyTypeTranslationKey } from '@/constants/appointmentOptions';
import { formatPriceFromMinorUnits } from '@/utils/moneyFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { resolveLocalizedName } from '@/components/practitioner/TreatmentLibrary/localizedName';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { useAssistantTarget } from '@/components/assistant/uiActions/useAssistantTarget';
import { ServiceFormModal } from './ServiceFormModal';

export function ServicesSection() {
  const { t, i18n } = useTranslation('services');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [showInactive, setShowInactive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const addButtonTargetRef = useAssistantTarget(ASSISTANT_TARGET_KEY.servicesAddButton);

  const servicesQuery = useServicesQuery(debouncedSearch, showInactive, true);
  const setActiveMutation = useSetServiceActiveMutation();
  const services = servicesQuery.data === undefined ? [] : servicesQuery.data;

  function openAdd() {
    setEditing(null);
    setIsModalOpen(true);
  }

  function openEdit(service: Service) {
    setEditing(service);
    setIsModalOpen(true);
  }

  function toggleActive(service: Service) {
    setActiveMutation.mutate(
      { serviceId: service.id, isActive: !service.isActive },
      {
        onSuccess: function done() {
          showSuccessNotification(service.isActive ? t('deactivated') : t('activated'));
        },
        onError: function fail(error) {
          showErrorNotification(resolveApiErrorMessage(error));
        },
      }
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-zooindigo">{t('title')}</h2>
        <p className="text-sm text-zoomuted mt-0.5">{t('subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="flex-1 min-w-0 flex items-center gap-2.5 bg-zoowhite border border-zooborder rounded-xl px-3.5">
          <Icon name="Search" className="text-zoomuted" />
          <input
            type="search"
            value={searchInput}
            placeholder={t('searchPlaceholder')}
            onChange={function update(changeEvent) {
              setSearchInput(changeEvent.target.value);
            }}
            className="flex-1 min-w-0 bg-transparent border-none outline-none py-2.5 text-sm text-zooindigo"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2.5 text-[13px] font-semibold text-zooindigo whitespace-nowrap">
            <ToggleSwitch
              isChecked={showInactive}
              onToggle={function toggle() {
                setShowInactive(function invert(previous) {
                  return previous === false;
                });
              }}
              accessibleLabel={t('showInactive')}
            />
            {t('showInactive')}
          </label>
          <Button
            ref={addButtonTargetRef}
            type="button"
            className="flex-shrink-0"
            onClick={openAdd}
          >
            <Icon name="Plus" /> {t('add')}
          </Button>
        </div>
      </div>

      {servicesQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : servicesQuery.isError === true || servicesQuery.data === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            servicesQuery.refetch();
          }}
        />
      ) : services.length === 0 ? (
        <Card
          padding="lg"
          variant="dashed"
          shadow={false}
          className="text-center text-sm text-zoomuted"
        >
          {debouncedSearch.trim().length > 0 ? t('noResults') : t('empty')}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {services.map(function renderCard(service) {
            return (
              <ServiceCard
                key={service.id}
                service={service}
                language={i18n.language}
                onEdit={openEdit}
                onToggleActive={toggleActive}
                isTogglingActive={
                  setActiveMutation.isPending &&
                  setActiveMutation.variables !== undefined &&
                  setActiveMutation.variables.serviceId === service.id
                }
              />
            );
          })}
        </div>
      )}

      {isModalOpen ? (
        <ServiceFormModal
          service={editing}
          onClose={function close() {
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface ServiceCardProps {
  service: Service;
  language: string;
  onEdit: (service: Service) => void;
  onToggleActive: (service: Service) => void;
  isTogglingActive: boolean;
}

function ServiceCard({
  service,
  language,
  onEdit,
  onToggleActive,
  isTogglingActive,
}: ServiceCardProps) {
  const { t } = useTranslation('services');
  const localizedName = resolveLocalizedName(service.nameEn, service.namePl);
  const localizedDescription = resolveLocalizedName(
    service.descriptionEn === null ? '' : service.descriptionEn,
    service.descriptionPl === null ? '' : service.descriptionPl
  );
  const formattedPrice = formatPriceFromMinorUnits(
    service.priceMinorUnits,
    service.currency,
    language
  );

  return (
    <Card
      as="article"
      padding="none"
      className={service.isActive ? 'p-4 flex flex-col gap-3' : 'p-4 flex flex-col gap-3 opacity-60'}
    >
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
          <Icon name="Tag" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-bold text-zooindigo truncate">{localizedName}</div>
          <div className="text-xs font-bold text-zooprimary">
            {formattedPrice === null ? t('priceOnRequest') : formattedPrice}
          </div>
        </div>
        {service.therapyType === null ? null : (
          <Badge uppercase className="px-2.5 py-1 whitespace-nowrap">
            {t(buildTherapyTypeTranslationKey(service.therapyType))}
          </Badge>
        )}
      </div>

      {localizedDescription.length === 0 ? null : (
        <p className="text-[13px] text-zoomuted whitespace-pre-line line-clamp-3">
          {localizedDescription}
        </p>
      )}

      <div className="flex items-center justify-between border-t border-zooborder pt-2.5 mt-0.5">
        <span className="text-xs text-zoomuted">
          {service.isActive ? '' : t('inactiveLabel')}
        </span>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('form.editTitle')}
            onClick={function edit() {
              onEdit(service);
            }}
          >
            <Icon name="Edit" />
          </Button>
          <ToggleSwitch
            isChecked={service.isActive}
            isPending={isTogglingActive}
            onToggle={function toggle() {
              onToggleActive(service);
            }}
            accessibleLabel={t('form.activeLabel')}
          />
        </div>
      </div>
    </Card>
  );
}
